import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export function formatApiError(error) {
  const detail = error.response?.data?.detail;
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Modules (top-level, was courses)
export const getModules = async () => {
  const response = await axios.get(`${API}/courses`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
export const getCourses = getModules; // alias for backward compat

export const getModule = async (id) => {
  const response = await axios.get(`${API}/courses/${id}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
export const getCourse = getModule;

export const createModule = async (data) => {
  const response = await axios.post(`${API}/courses`, data, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
export const createCourse = createModule;

export const updateModule = async (id, data) => {
  const response = await axios.put(`${API}/courses/${id}`, data, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
export const updateCourse = updateModule;

export const deleteModule = async (id) => {
  const response = await axios.delete(`${API}/courses/${id}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
export const deleteCourse = deleteModule;

// Subjects (courses within a module)
export const getSubjects = async (moduleId) => {
  const response = await axios.get(`${API}/courses/${moduleId}/subjects`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const createSubject = async (moduleId, data) => {
  const response = await axios.post(`${API}/courses/${moduleId}/subjects`, data, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const updateSubject = async (subjectId, data) => {
  const response = await axios.put(`${API}/subjects/${subjectId}`, data, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const deleteSubject = async (subjectId) => {
  const response = await axios.delete(`${API}/subjects/${subjectId}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Documents
export const uploadDocument = async (moduleId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/courses/${moduleId}/documents`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true
  });
  return response.data;
};

export const deleteDocument = async (moduleId, docId) => {
  const response = await axios.delete(`${API}/courses/${moduleId}/documents/${docId}`, {
    headers: getAuthHeaders(), withCredentials: true
  });
  return response.data;
};

export const getDocumentUrl = (storagePath) => {
  const token = localStorage.getItem('access_token');
  return `${API}/documents/${storagePath}?auth=${token}`;
};

// Live Classes
export const getLiveClasses = async (courseId, subjectId) => {
  let url = `${API}/live-classes`;
  const params = [];
  if (subjectId) params.push(`subject_id=${subjectId}`);
  else if (courseId) params.push(`course_id=${courseId}`);
  if (params.length) url += '?' + params.join('&');
  const response = await axios.get(url, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const createLiveClass = async (data) => {
  const response = await axios.post(`${API}/live-classes`, data, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const deleteLiveClass = async (id) => {
  const response = await axios.delete(`${API}/live-classes/${id}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getLiveClass = async (id) => {
  const response = await axios.get(`${API}/live-classes/${id}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const addRecording = async (classId, recordingUrl, recordingTitle) => {
  const response = await axios.put(`${API}/live-classes/${classId}/recording`,
    { recording_url: recordingUrl, recording_title: recordingTitle },
    { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getCourseRecordings = async (moduleId) => {
  const response = await axios.get(`${API}/courses/${moduleId}/recordings`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Progress
export const markProgress = async (itemId, itemType = 'recording', completed = true) => {
  const response = await axios.post(`${API}/progress/mark`,
    { item_id: itemId, item_type: itemType, completed },
    { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getModuleProgress = async (moduleId) => {
  const response = await axios.get(`${API}/progress/module/${moduleId}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
export const getCourseProgress = getModuleProgress;

export const getSubjectProgress = async (subjectId) => {
  const response = await axios.get(`${API}/progress/subject/${subjectId}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getAllProgress = async () => {
  const response = await axios.get(`${API}/progress/my`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Certificates
export const checkCertificateEligibility = async (courseId) => {
  const response = await axios.get(`${API}/certificates/check/${courseId}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const generateCertificate = async (courseId) => {
  const response = await axios.post(`${API}/certificates/generate/${courseId}`, {}, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getCertificate = async (certificateId) => {
  const response = await axios.get(`${API}/certificates/${certificateId}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getMyCertificates = async () => {
  const response = await axios.get(`${API}/certificates/my/all`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const verifyCertificate = async (certificateNumber) => {
  const response = await axios.get(`${API}/certificates/verify/${certificateNumber}`, { withCredentials: true });
  return response.data;
};

// Enrollments
export const getMyEnrollments = async () => {
  const response = await axios.get(`${API}/enrollments/my`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const enrollInCourse = async (courseId, subscriptionType) => {
  const response = await axios.post(`${API}/enrollments`,
    { course_id: courseId, subscription_type: subscriptionType },
    { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Payments - Flow.cl
export const createFlowPayment = async (courseId, subscriptionType) => {
  const response = await axios.post(`${API}/payments/create`,
    { course_id: courseId, subscription_type: subscriptionType },
    { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const checkPaymentByToken = async (token) => {
  const response = await axios.get(`${API}/payments/check-by-token/${token}`, { withCredentials: true });
  return response.data;
};

export const checkPaymentStatus = async (commerceOrder) => {
  const response = await axios.get(`${API}/payments/check/${commerceOrder}`, { withCredentials: true });
  return response.data;
};

export const getMyPayments = async () => {
  const response = await axios.get(`${API}/payments/my`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Teacher
export const getTeacherSubjects = async () => {
  const response = await axios.get(`${API}/teacher/subjects`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
export const getTeacherCourses = getTeacherSubjects;

export const getTeacherStats = async () => {
  const response = await axios.get(`${API}/teacher/stats`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Admin
export const getAdminStats = async () => {
  const response = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getAdminUsers = async () => {
  const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const updateUserRole = async (userId, role) => {
  const response = await axios.put(`${API}/admin/users/${userId}/role?role=${role}`, {}, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getAdminPayments = async () => {
  const response = await axios.get(`${API}/admin/payments`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getAdminEnrollments = async () => {
  const response = await axios.get(`${API}/admin/enrollments`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getAdminStudents = async () => {
  const response = await axios.get(`${API}/admin/students`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const createManualEnrollment = async (data) => {
  const response = await axios.post(`${API}/admin/enrollments`, data, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getTeachers = async () => {
  const response = await axios.get(`${API}/admin/teachers`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Grades
export const getGradeColumns = async (subjectId) => {
  const response = await axios.get(`${API}/subjects/${subjectId}/grade-columns`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const createGradeColumn = async (subjectId, title) => {
  const response = await axios.post(`${API}/subjects/${subjectId}/grade-columns`, { title }, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const deleteGradeColumn = async (columnId) => {
  const response = await axios.delete(`${API}/grade-columns/${columnId}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const setGrade = async (subjectId, studentId, columnId, value) => {
  const response = await axios.post(`${API}/subjects/${subjectId}/grades`, { student_id: studentId, column_id: columnId, value }, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getSubjectGrades = async (subjectId) => {
  const response = await axios.get(`${API}/subjects/${subjectId}/grades`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getMyGrades = async (subjectId) => {
  const response = await axios.get(`${API}/subjects/${subjectId}/my-grades`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Attendance
export const setAttendance = async (classId, studentId, status) => {
  const response = await axios.post(`${API}/live-classes/${classId}/attendance`, { student_id: studentId, status }, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const setAttendanceBulk = async (classId, records) => {
  const response = await axios.post(`${API}/live-classes/${classId}/attendance/bulk`, { records }, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getSubjectAttendance = async (subjectId) => {
  const response = await axios.get(`${API}/subjects/${subjectId}/attendance`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const getMyAttendance = async (subjectId) => {
  const response = await axios.get(`${API}/subjects/${subjectId}/my-attendance`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

// Subject Documents (per course, by teacher)
export const uploadSubjectDocument = async (subjectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/subjects/${subjectId}/documents`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true
  });
  return response.data;
};

export const getSubjectDocuments = async (subjectId) => {
  const response = await axios.get(`${API}/subjects/${subjectId}/documents`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};

export const deleteSubjectDocument = async (subjectId, docId) => {
  const response = await axios.delete(`${API}/subjects/${subjectId}/documents/${docId}`, { headers: getAuthHeaders(), withCredentials: true });
  return response.data;
};
